import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { isShortcutHelpEvent } from "../keyboard-shortcuts";
import {
  isEditableKeyboardTarget,
  shortcutLabel,
} from "../markdown/document-session";
import {
  visibleHeaderQuickActions,
  workspaceComposePath,
} from "../workspace-chrome";
import KeyboardShortcutsDialog from "./KeyboardShortcutsDialog";

export function HeaderQuickActions({
  organizationId,
  canAdminister,
}: {
  readonly organizationId: string;
  readonly canAdminister: boolean;
}) {
  const navigate = useNavigate();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const actions = visibleHeaderQuickActions(canAdminister);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isShortcutHelpEvent(event)) {
        return;
      }
      if (isEditableKeyboardTarget(event.target)) {
        return;
      }
      event.preventDefault();
      setShortcutsOpen((current) => !current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        rootRef.current?.contains(event.target) !== true
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="header-quick-actions">
      <div className="header-quick-actions__compose" ref={rootRef}>
        <button
          className="header-icon-button"
          type="button"
          aria-expanded={open}
          aria-controls={menuId}
          aria-haspopup="menu"
          aria-label="Add"
          title="Add"
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          <HeaderIcon name="add" />
        </button>
        {open ? (
          <div className="header-quick-actions__menu" id={menuId} role="menu">
            {actions.map((item) => (
              <button
                key={item.action}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  void navigate(
                    workspaceComposePath(organizationId, item.action),
                  );
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <Link
        className="header-icon-button"
        to={`/org/${organizationId}/search`}
        aria-label="Search"
        title="Search"
      >
        <HeaderIcon name="search" />
      </Link>
      <button
        className="header-icon-button"
        type="button"
        aria-label="Keyboard shortcuts"
        title={`Keyboard shortcuts (${shortcutLabel("/")})`}
        onClick={() => {
          setShortcutsOpen(true);
        }}
      >
        <HeaderIcon name="shortcuts" />
      </button>
      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />
    </div>
  );
}

function HeaderIcon({
  name,
}: {
  readonly name: "add" | "search" | "shortcuts";
}) {
  return (
    <svg
      className="header-icon-button__glyph"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="square"
      strokeLinejoin="miter"
    >
      {headerIconGraphic(name)}
    </svg>
  );
}

function headerIconGraphic(name: "add" | "search" | "shortcuts"): ReactNode {
  if (name === "search") {
    return (
      <>
        <circle cx="8.5" cy="8.5" r="4.2" />
        <path d="M11.7 11.7 16 16" />
      </>
    );
  }
  if (name === "shortcuts") {
    return (
      <>
        <rect x="3.2" y="5.2" width="13.6" height="9.6" rx="1.2" />
        <path d="M6.2 12.2h7.6" />
        <path d="M6.8 8.2h2" />
        <path d="M11.2 8.2h2" />
      </>
    );
  }
  return (
    <>
      <path d="M10 4.5v11" />
      <path d="M4.5 10h11" />
    </>
  );
}
